const API_BASE = (import.meta as any).env?.VITE_API_BASE || '/v1';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  credentials?: 'include' | 'same-origin' | 'omit';
  params?: Record<string, string | number | boolean>;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    
    const url = new URL(`${this.baseUrl}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    const response = await fetch(url.toString(), {
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async get<T>(endpoint: string, params?: Record<string, string | boolean | number>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, data?: any, params?: Record<string, string | boolean | number>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      params,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    const response = await fetch(url.toString(), {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }
}

export const api = new ApiClient();

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
}

// ============================================
// Auth API
// ============================================
export const authApi = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: { email: string; password: string; firstName: string; lastName: string; referralCode?: string }) => 
    api.post<ApiResponse<any>>('/auth/register', data),
  logout: () => api.post<ApiResponse<any>>('/auth/logout', {}),
  me: () => api.get<ApiResponse<any>>('/auth/me'),
  refresh: () => api.post<ApiResponse<any>>('/auth/refresh', {}),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token: string) => api.post('/auth/verify-email', { token }),
  updateProfile: (data: { email?: string; firstName?: string; lastName?: string; phone?: string }) => 
    api.patch<ApiResponse<any>>('/auth/profile', data),
  changePassword: (currentPassword: string, newPassword: string) => 
    api.post('/auth/change-password', { currentPassword, newPassword }),
  deleteAccount: (password?: string) => api.delete<ApiResponse<any>>('/auth/account', { password }),
  getSessions: () => api.get<ApiResponse<any[]>>('/auth/sessions'),
  revokeSession: (sessionId: string) => api.delete<ApiResponse<any>>(`/auth/sessions/${sessionId}`),
  revokeAllSessions: () => api.delete<ApiResponse<any>>('/auth/sessions'),
  // 2FA
  getTotpSetup: () => api.get<ApiResponse<any>>('/auth/totp/setup'),
  verifyTotpSetup: (token: string) => api.post<ApiResponse<any>>('/auth/totp/verify-setup', { token }),
  disableTotp: (token: string) => api.post<ApiResponse<any>>('/auth/totp/disable', { token }),
  getBackupCodes: () => api.get<ApiResponse<any[]>>('/auth/totp/backup-codes'),
  generateBackupCodes: () => api.post<ApiResponse<any[]>>('/auth/totp/backup-codes/generate'),
  verifyTotpForLogin: (token: string) => api.post<ApiResponse<any>>('/auth/totp/verify-login', { token }),
};

// ============================================
// Numbers API
// ============================================
export const numbersApi = {
  getNumbers: (params?: { page?: number; limit?: number; country?: string; status?: string }) => 
    api.get<ApiResponse<any[]>>('/v1/numbers', params),
  getAssignments: (params?: { page?: number; limit?: number; status?: string }) => 
    api.get<ApiResponse<any[]>>('/v1/assignments', params),
  assignNumber: (data: { country: string; type?: string; providerId?: string }) => 
    api.post<ApiResponse<any>>('/v1/numbers/assign', data),
  releaseNumber: (assignmentId: string) => api.post<ApiResponse<any>>(`/v1/numbers/release`, { assignmentId }),
  getNumber: (id: string) => api.get<ApiResponse<any>>(`/v1/numbers/${id}`),
  updateNumber: (id: string, data: any) => api.put<ApiResponse<any>>(`/v1/numbers/${id}`, data),
  deleteNumber: (id: string) => api.delete<ApiResponse<any>>(`/v1/numbers/${id}`),
};

// ============================================
// Messages API
// ============================================
export const messagesApi = {
  getMessages: (params?: { page?: number; limit?: number; assignmentId?: string }) => {
    if (params && params.assignmentId) {
      const { assignmentId, ...rest } = params;
      return api.get<ApiResponse<any[]>>(`/v1/assignments/${assignmentId}/messages`, rest);
    }
    return api.get<ApiResponse<any[]>>('/v1/messages', params);
  },
  sendSms: (data: { to: string; from: string; body: string }) => api.post<ApiResponse<any>>('/v1/messages/sms', data),
  getMessage: (id: string) => api.get<ApiResponse<any>>(`/v1/messages/${id}`),
};

// ============================================
// Voice API
// ============================================
export const voiceApi = {
  getEvents: (params?: { page?: number; limit?: number; assignmentId?: string }) => {
    if (params && params.assignmentId) {
      const { assignmentId, ...rest } = params;
      return api.get<ApiResponse<any[]>>(`/v1/assignments/${assignmentId}/voice`, rest);
    }
    return api.get<ApiResponse<any[]>>('/v1/voice/events', params);
  },
  makeCall: (data: { to: string; from: string; webhookUrl?: string }) => api.post<ApiResponse<any>>('/v1/voice/call', data),
  getEvent: (id: string) => api.get<ApiResponse<any>>(`/v1/voice/events/${id}`),
  getRecording: (id: string) => api.get(`/v1/voice/events/${id}/recording`),
  getTranscription: (id: string) => api.get(`/v1/voice/events/${id}/transcription`),
};

// ============================================
// Wallet API
// ============================================
export const walletApi = {
  getBalance: () => api.get<ApiResponse<any[]>>('/v1/wallet'),
  getTransactions: (params?: { page?: number; limit?: number; type?: string }) => 
    api.get<ApiResponse<any[]>>('/v1/wallet/transactions', params),
  getWithdrawals: (params?: { page?: number; limit?: number; status?: string }) => 
    api.get<ApiResponse<any[]>>('/v1/withdrawals', params),
  requestWithdrawal: (data: { amount: number; currency: string; address?: string; payoutMethodId?: string }) => 
    api.post<ApiResponse<any>>('/v1/withdrawals', data),
  getPayoutMethods: () => api.get<ApiResponse<any[]>>('/v1/payout-methods'),
  addPayoutMethod: (data: { type: string; details: Record<string, string>; label?: string }) => 
    api.post<ApiResponse<any>>('/v1/payout-methods', data),
  deletePayoutMethod: (id: string) => api.delete<ApiResponse<any>>(`/v1/payout-methods/${id}`),
  setDefaultPayoutMethod: (id: string) => api.post<ApiResponse<any>>(`/v1/payout-methods/${id}/default`),
};

// ============================================
// Developer API (API Keys & Webhooks)
// ============================================
export const developerApi = {
  getApiKeys: () => api.get<ApiResponse<any[]>>('/v1/api-keys'),
  createApiKey: (data: { name: string; permissions: string[]; rateLimit?: number; expiresIn?: number }) => 
    api.post<ApiResponse<any>>('/v1/api-keys', data),
  revokeApiKey: (id: string) => api.delete<ApiResponse<any>>(`/v1/api-keys/${id}`),
  getWebhooks: () => api.get<ApiResponse<any>>('/v1/webhooks'),
  updateWebhook: (data: { url: string; secret: string }) => 
    api.put<ApiResponse<any>>('/v1/webhooks', data),
};

// ============================================
// Notifications API
// ============================================
export const notificationsApi = {
  getNotifications: (params?: { page?: number; limit?: number; unreadOnly?: boolean; type?: string }) => 
    api.get('/v1/notifications', params),
  getUnreadCount: () => api.get('/v1/notifications/unread-count'),
  markAsRead: (id: string) => api.post(`/v1/notifications/${id}/read`),
  markAllAsRead: () => api.post('/v1/notifications/read-all'),
  deleteNotification: (id: string) => api.delete(`/v1/notifications/${id}`),
};

// ============================================
// Admin API
// ============================================
export const adminApi = {
  // Dashboard
  getStats: () => api.get('/admin/'),
  getProvidersHealth: () => api.get('/admin/providers/health'),

  // Users
  getUsers: (params?: string) => api.get(`/admin/users?${params}`),
  getUser: (id: string) => api.get(`/admin/users/${id}`),
  suspendUser: (id: string) => api.post(`/admin/users/${id}/suspend`),
  unsuspendUser: (id: string) => api.post(`/admin/users/${id}/unsuspend`),
  banUser: (id: string) => api.post(`/admin/users/${id}/ban`),
  unbanUser: (id: string) => api.post(`/admin/users/${id}/unban`),
  resetUserPassword: (id: string) => api.post(`/admin/users/${id}/reset-password`),
  updateUserTier: (id: string, tier: string) => api.post(`/admin/users/${id}/tier`, { tier }),
  updateUserRole: (id: string, role: string) => api.post(`/admin/users/${id}/role`, { role }),

  // Providers
  getProviders: (params?: string) => api.get(`/admin/providers?${params}`),
  createProvider: (data: any) => api.post('/admin/providers', data),
  getProvider: (id: string) => api.get(`/admin/providers/${id}`),
  updateProvider: (id: string, data: any) => api.put(`/admin/providers/${id}`, data),
  deleteProvider: (id: string) => api.delete(`/admin/providers/${id}`),
  toggleProviderStatus: (id: string) => api.post(`/admin/providers/${id}/toggle-status`),
  setProviderMode: (id: string, mode: string) => api.post(`/admin/providers/${id}/set-mode`, { mode }),
  testProviderConnection: (id: string) => api.post(`/admin/providers/${id}/test-connection`),
  cloneProvider: (id: string) => api.post(`/admin/providers/${id}/clone`),
  getProviderMetricsById: (id: string) => api.get(`/admin/providers/${id}/metrics`),
  getProviderLogs: (id: string) => api.get(`/admin/providers/${id}/logs`),
  importProviderCsv: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload(`/admin/providers/${id}/import`, formData);
  },
  exportProviderCsv: (id: string) => api.get(`/admin/providers/${id}/export`),

  // Numbers
  getNumbers: (params?: string) => api.get(`/admin/numbers/pool?${params}`),
  createNumber: (data: any) => api.post('/admin/numbers/pool', data),
  getNumber: (id: string) => api.get(`/admin/numbers/pool/${id}`),
  updateNumber: (id: string, data: any) => api.put(`/admin/numbers/pool/${id}`, data),
  deleteNumber: (id: string) => api.delete(`/admin/numbers/pool/${id}`),
  bulkActionNumbers: (action: string, ids: string[], data?: any) => 
    api.post('/admin/numbers/pool/bulk-action', { action, ids, data }),
  getNumberBatches: () => api.get('/admin/numbers/pool/batches'),
  getNumberAudit: (id: string) => api.get(`/admin/numbers/pool/audit/${id}`),
  importNumbers: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload('/admin/numbers/pool/import', formData);
  },
  exportNumbers: () => api.get('/admin/numbers/pool/export'),
  getAssignments: (params?: string) => api.get(`/admin/assignments?${params}`),
  releaseAssignment: (assignmentId: string) => api.post(`/admin/assignments/${assignmentId}/release`),

  // Settlements
  getSettlements: (params?: string) => api.get(`/admin/settlements?${params}`),
  getProviderSettlements: (params?: string) => api.get(`/admin/settlements/provider?${params}`),
  getPayouts: (params?: string) => api.get(`/admin/payouts?${params}`),
  processSettlement: (id: string) => api.post(`/admin/settlements/${id}/process`),
  createPayout: (data: any) => api.post('/admin/payouts', data),
  approvePayout: (id: string) => api.post(`/admin/payouts/${id}/approve`),
  rejectPayout: (id: string, reason: string) => api.post(`/admin/payouts/${id}/reject`, { reason }),

  // Analytics
  getMetrics: (params?: string) => api.get(`/admin/metrics?${params}`),
  getProviderMetrics: (params?: string) => api.get(`/admin/metrics/providers?${params}`),
  getUserMetrics: (params?: string) => api.get(`/admin/metrics/users?${params}`),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data: any) => api.put('/admin/settings', data),
  getGlobalSettings: () => api.get('/admin/settings/global'),
  updateGlobalSettings: (data: any) => api.put('/admin/settings/global', data),
  getRewardSettings: () => api.get('/admin/engines/reward/rules'),
  updateRewardSettings: (data: any) => api.post('/admin/engines/reward/rules', data),
  getProviderSettings: () => api.get('/admin/settings/providers'),
  updateProviderSettings: (data: any) => api.put('/admin/settings/providers', data),
  getWalletSettings: () => api.get('/admin/settings/wallet'),
  updateWalletSettings: (data: any) => api.put('/admin/settings/wallet', data),
  getSettlementSettings: () => api.get('/admin/settings/settlement'),
  updateSettlementSettings: (data: any) => api.put('/admin/settings/settlement', data),
  getFraudSettings: () => api.get('/admin/settings/fraud'),
  updateFraudSettings: (data: any) => api.put('/admin/settings/fraud', data),
  getRiskSettings: () => api.get('/admin/settings/risk'),
  updateRiskSettings: (data: any) => api.put('/admin/settings/risk', data),
  getNotificationSettings: () => api.get('/admin/settings/notifications'),
  updateNotificationSettings: (data: any) => api.put('/admin/settings/notifications', data),
  getWebhookSettings: () => api.get('/admin/settings/webhooks'),
  updateWebhookSettings: (data: any) => api.put('/admin/settings/webhooks', data),
  getUserTierSettings: () => api.get('/admin/settings/user-tiers'),
  updateUserTierSettings: (data: any) => api.put('/admin/settings/user-tiers', data),
  getResellerSettings: () => api.get('/admin/settings/resellers'),
  updateResellerSettings: (data: any) => api.put('/admin/settings/resellers', data),
  getMasterResellerSettings: () => api.get('/admin/settings/master-resellers'),
  updateMasterResellerSettings: (data: any) => api.put('/admin/settings/master-resellers', data),

  // Provider Analytics
  getProviderAnalyticsOverview: () => api.get('/admin/providers/analytics/overview'),
  getProviderAnalytics: (id: string) => api.get(`/admin/providers/analytics/${id}`),
  getProviderTraffic: (id: string, params?: string) => api.get(`/admin/providers/analytics/${id}/traffic?${params}`),
  getProviderPerformance: (id: string) => api.get(`/admin/providers/analytics/${id}/performance`),
  getProviderFinancial: (id: string) => api.get(`/admin/providers/analytics/${id}/financial`),
  getProviderQuality: (id: string) => api.get(`/admin/providers/analytics/${id}/quality`),
  getProviderIncidents: (id: string) => api.get(`/admin/providers/analytics/${id}/incidents`),
  compareProviders: (params?: string) => api.get(`/admin/providers/analytics/compare?${params}`),
  exportProviderAnalytics: (params?: string) => api.get(`/admin/providers/analytics/export?${params}`),

  // Generic request method
  request: async (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', endpoint: string, data?: any) => api.request(endpoint, { method, body: data ? JSON.stringify(data) : undefined }),
};

export default api;
